/**
 * useSettlementUpload Hook
 * 정산서 업로드 관련 로직 관리
 *
 * @description
 * - 업로드 이력 조회
 * - 협력사/라이더 정산서 업로드
 * - 업로드 적용 처리
 */
import {useCallback, useEffect, useState} from 'react';
import {settlementUploadApi} from '../api/settlementUploadApi';
import {GetUploadHistoryRequest, SettlementUploadHistory, SettlementUploadType,} from '../types/settlementUpload.types';
import {useToast} from '@/shared/components/feedback/Toast/ToastContext';
import {ToastType} from '@/shared/components/feedback/Toast/Toast.types';
import {getItem} from '@/shared/utils/storage';

/**
 * 정산서 업로드 관리 훅
 */
export const useSettlementUpload = () => {
    const { showToast } = useToast();

    // 로딩 상태
    const [historyLoading, setHistoryLoading] = useState(false);
    const [uploadLoading, setUploadLoading] = useState(false);
    const [applyLoading, setApplyLoading] = useState(false);

    // 데이터 상태
    const [histories, setHistories] = useState<SettlementUploadHistory[]>([]);
    const [totalCount, setTotalCount] = useState(0);

    /**
     * 업로드 이력 조회
     */
    const fetchUploadHistory = useCallback(async (request?: GetUploadHistoryRequest) => {
        try {
            setHistoryLoading(true);
            const response = await settlementUploadApi.getUploadHistory(request);
            // 응답이 정상적으로 왔으면 (빈 배열도 정상)
            setHistories(response?.histories || []);
            setTotalCount(response?.totalCount || 0);
        } catch (error) {
            // 네트워크 에러나 서버 에러인 경우에만 에러 토스트 표시
            console.error('업로드 이력 조회 실패:', error);
            // 에러 시에도 빈 배열로 초기화하고, 심각한 에러가 아닌 경우 토스트 표시 안함
            setHistories([]);
            setTotalCount(0);
            // 네트워크 에러 등 심각한 경우에만 토스트 표시
            if (error instanceof Error && error.message.includes('Network')) {
                showToast('서버와 연결할 수 없습니다.', { type: ToastType.ERROR });
            }
        } finally {
            setHistoryLoading(false);
        }
    }, [showToast]);

    /**
     * 정산서 업로드
     * @param uploadType 업로드 타입 (PARTNER | RIDER)
     * @param file 엑셀 파일
     */
    const uploadSettlement = useCallback(async (
        uploadType: SettlementUploadType,
        file: File
    ): Promise<boolean> => {
        try {
            setUploadLoading(true);
            const userId = getItem<string>('USER_ID') || undefined;
            const typeName = uploadType === 'PARTNER' ? '협력사' : '라이더';

            const response = await settlementUploadApi.uploadSettlement(uploadType, file, userId);

            if (response.success) {
                showToast(`${typeName} 정산서가 성공적으로 업로드되었습니다.`, { type: ToastType.SUCCESS });
                // 업로드 성공 후 이력 새로고침
                await fetchUploadHistory();
                return true;
            } else {
                showToast(response.message || `${typeName} 정산서 업로드에 실패했습니다.`, { type: ToastType.ERROR });
                return false;
            }
        } catch (error: unknown) {
            const typeName = uploadType === 'PARTNER' ? '협력사' : '라이더';
            // ApiError 또는 axios 에러 응답에서 메시지 추출
            // 인터셉터가 ApiError로 변환한 경우 error.message에 메시지가 있음
            const apiError = error as { message?: string; response?: { data?: { message?: string } } };
            const errorMessage = apiError?.message || apiError?.response?.data?.message || `${typeName} 정산서 업로드에 실패했습니다.`;

            showToast(errorMessage, { type: ToastType.ERROR });
            return false;
        } finally {
            setUploadLoading(false);
        }
    }, [showToast, fetchUploadHistory]);

    /**
     * 업로드 적용
     * @param uploadId 업로드 ID
     */
    const applyUpload = useCallback(async (uploadId: string): Promise<boolean> => {
        try {
            setApplyLoading(true);
            const response = await settlementUploadApi.applyUpload(uploadId);

            if (response.success) {
                showToast('정산서가 성공적으로 적용되었습니다.', { type: ToastType.SUCCESS });
                // 적용 성공 후 이력 새로고침
                await fetchUploadHistory();
                return true;
            } else {
                showToast(response.message || '정산서 적용에 실패했습니다.', { type: ToastType.ERROR });
                return false;
            }
        } catch (error) {
            showToast('정산서 적용에 실패했습니다.', { type: ToastType.ERROR });
            return false;
        } finally {
            setApplyLoading(false);
        }
    }, [showToast, fetchUploadHistory]);

    /**
     * 업로드 취소
     * @param uploadId 업로드 ID
     */
    const cancelUpload = useCallback(async (uploadId: string): Promise<boolean> => {
        try {
            setApplyLoading(true);
            const response = await settlementUploadApi.cancelUpload(uploadId);

            if (response.success) {
                showToast('업로드가 취소되었습니다.', { type: ToastType.SUCCESS });
                // 취소 성공 후 이력 새로고침
                await fetchUploadHistory();
                return true;
            } else {
                showToast(response.message || '업로드 취소에 실패했습니다.', { type: ToastType.ERROR });
                return false;
            }
        } catch (error) {
            showToast('업로드 취소에 실패했습니다.', { type: ToastType.ERROR });
            return false;
        } finally {
            setApplyLoading(false);
        }
    }, [showToast, fetchUploadHistory]);

    // 컴포넌트 마운트 시 업로드 이력 조회
    useEffect(() => {
        fetchUploadHistory();
    }, [fetchUploadHistory]);

    return {
        // 로딩 상태
        historyLoading,
        uploadLoading,
        applyLoading,
        loading: historyLoading || uploadLoading || applyLoading,

        // 데이터
        histories,
        totalCount,

        // 함수
        fetchUploadHistory,
        uploadSettlement,
        applyUpload,
        cancelUpload,
    };
};
